// Grid system constants
export const ITEMS_PER_COLUMN = 15      // Max items per column before overflow
export const MAX_CONTENT_GROUPS = 6     // Max groups across the 12-column grid (6 × 2 cols = 12)
export const TOTAL_GRID_COLUMNS = 12    // Total grid columns
export const COLS_PER_GROUP = 2         // Each content group occupies 2 grid columns

/**
 * Returns the remaining grid column span after subtracting the provided sibling spans.
 * Useful when sibling spans are dynamic and the first child should fill the rest.
 * e.g. getRemainingColSpan(3, 6) → 3  (12 - 3 - 6)
 */
export const getRemainingColSpan = (...siblingSpans) => {
    const used = siblingSpans.reduce((sum, span) => sum + span, 0)
    return Math.max(1, TOTAL_GRID_COLUMNS - used)
}

/**
 * Maps an illustration block's authored width to a grid column span.
 *
 * Supports two block types:
 *  - `GDIContentTileBlock` (new): reads `GDIContentTileBlockStylesPerViewType[].ColSpan`,
 *    preferring an entry whose `ViewTypesToApply` includes `WebDesktop`. If none
 *    matches the view type, the first entry that has a `ColSpan` is used.
 *  - `MegaMenuIlustrationBlock` (legacy): reads `IllustrationWidth`.
 *
 * Accepted values (case-insensitive): "Small" | "Medium" | "Large" or "2" | "3" | "6".
 * Defaults to Small (2 grid columns) when unset.
 */
const WIDTH_TO_SPAN = { small: 2, medium: 3, large: 6, '2': 2, '3': 3, '6': 6 }

const findColSpanForViewType = (styles, viewType) => {
    if (!Array.isArray(styles)) return null
    const matchByViewType = styles.find((s) => {
        const tokens = String(s?.ViewTypesToApply || '')
            .split(',')
            .map((t) => t.trim())
        return tokens.includes(viewType) && s?.ColSpan
    })
    if (matchByViewType?.ColSpan) return matchByViewType.ColSpan
    const anyWithColSpan = styles.find((s) => s?.ColSpan)
    return anyWithColSpan?.ColSpan || null
}

export const getImageColSpan = (illustrationBlock) => {
    const expandedBlock =
        illustrationBlock?.ContentLink?.Expanded ||
        illustrationBlock?.Expanded ||
        illustrationBlock

    // 1. New ContentTile block — ColSpan per view type
    const tileColSpan = findColSpanForViewType(
        expandedBlock?.GDIContentTileBlockStylesPerViewType,
        'WebDesktop'
    )
    if (tileColSpan) {
        const span = WIDTH_TO_SPAN[String(tileColSpan).toLowerCase()]
        if (span) return span
    }

    // 2. Legacy MegaMenuIlustrationBlock — IllustrationWidth
    const legacyWidth = expandedBlock?.IllustrationWidth?.toLowerCase()
    if (WIDTH_TO_SPAN[legacyWidth]) return WIDTH_TO_SPAN[legacyWidth]

    // 3. ContentTile blocks default to Large (6) when nothing is authored;
    //    legacy MegaMenuIlustrationBlock falls back to Small (2).
    const isContentTile = expandedBlock?.__typename === 'GDIContentTileBlock'
    return isContentTile ? 6 : 2
}

/**
 * Helper to build category URL.
 * The item should already have c_relativeUrl populated from the API response.
 * Falls back to categories map as secondary lookup.
 */
export const getCategoryUrl = (item, categories) => {
    if (item?.c_relativeUrl) {
        return encodeURI(item.c_relativeUrl)
    }

    const fullCategory = categories?.[item?.id]
    if (fullCategory?.c_relativeUrl) {
        return encodeURI(fullCategory.c_relativeUrl)
    }

    return null
}

/**
 * Normalize a GDICtaBlock expanded block so it renders correctly inside the
 * navigation (both L0 triggers — coming through CategoryAddReplace — and the
 * inner mega-menu columns). Defaults the Style to `Mega Menu Link` whenever
 * the author left it empty or picked the legacy `Underlined on Hover` value
 * so every nav CTA picks up the shared animated underline + nav typography.
 * Explicit Styles (Button Solid, Outline, …) are passed through untouched so
 * authors can still opt into a custom look. Returns the input unchanged when
 * the block is not a GDICtaBlock.
 *
 * When the StylesPerViewType array is missing/empty, a single base entry
 * carrying `Style: 'Mega Menu Link'` is synthesized — otherwise the CTA would
 * fall back to the global DEFAULT_CTA_STYLE (`Button Solid`) inside the CTA
 * component.
 */
export const adaptCtaForNav = (expanded) => {
    if (expanded?.__typename !== 'GDICtaBlock') return expanded
    const styles = Array.isArray(expanded?.GDICtaBlockStylesPerViewType)
        ? expanded.GDICtaBlockStylesPerViewType
        : null
    if (!styles?.length) {
        return {
            ...expanded,
            GDICtaBlockStylesPerViewType: [
                {ViewTypesToApply: 'WebMobile,WebTablet,WebDesktop', Style: 'Mega Menu Link'}
            ]
        }
    }
    return {
        ...expanded,
        GDICtaBlockStylesPerViewType: styles.map((s) => {
            if (!s) return s
            const authored = typeof s.Style === 'string' ? s.Style.trim() : ''
            if (!authored || authored === 'Underlined on Hover') {
                return {...s, Style: 'Mega Menu Link'}
            }
            return s
        })
    }
}

/**
 * Drops authored color props so a nav CTA inherits the header's `currentColor`.
 * Used on the transparent header, where the text color flips with the
 * transparent / hovered / scrolled state.
 */
const COLOR_PROPS = ['color', 'backgroundColor', 'borderColor']

export const withoutColorProps = (props) => {
    if (!props) return props
    const rest = {...props}
    COLOR_PROPS.forEach((key) => delete rest[key])
    return rest
}
